"use client";

import * as React from "react";
import {
  ApiKeysAction,
  ApiKeysState,
  useApiKeysState,
} from "./api-keys-state.js";
import { ListMetadata } from "../../api/endpoint.js";

export interface ApiKeysContextType {
  state: ApiKeysState;
  dispatch: React.Dispatch<ApiKeysAction>;
}

const ApiKeysContext = React.createContext<ApiKeysContextType | undefined>(
  undefined,
);
ApiKeysContext.displayName = "ApiKeysContext";

const initialState: ApiKeysState = {
  pagination: null,
  searchQuery: null,
  createDialogOpen: false,
  createdApiKey: null,
};

export const ApiKeysContextProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const [state, dispatch] = useApiKeysState(initialState);

  const context = React.useMemo<ApiKeysContextType>(
    () => ({
      state,
      dispatch,
    }),
    [state, dispatch],
  );

  return (
    <ApiKeysContext.Provider value={context}>
      {children}
    </ApiKeysContext.Provider>
  );
};

const NOOP = () => void 0;
const EMPTY_PAGINATION: ListMetadata = { before: null, after: null };

/**
 * The context may be provided if it is instantiated in the tree above the user.
 */
export function useApiKeysContext(
  initialContext?: ApiKeysContextType | null,
): ApiKeysContextType {
  const context = React.useContext(ApiKeysContext);

  if (context) {
    return context;
  }

  if (initialContext) {
    return initialContext;
  }

  return {
    dispatch: NOOP,
    state: {
      pagination: EMPTY_PAGINATION,
      searchQuery: null,
      createDialogOpen: false,
      createdApiKey: null,
    },
  };
}
